import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons"
import { Col, Form, Input, Row, Select, Typography } from "antd"
import { useEffect } from "react"

import CopyToClipboard from "#blocks/CopyToClipboard"
import CreatableOptionSelect from "#blocks/CreatableOptionSelect"
import Flex from "#blocks/Flex"
import { SFormHeader } from "#blocks/Form/formComponents"
import { Student } from "#classes/Student"
import PinGenerator from "#components/PinGenerator"
import { USER_PRONOUN_OPTIONS, USER_ROLE_OPTIONS } from "#constants/userOptions"
import { useUserContext } from "#contexts/UserContextProvider"
import {
  RESOURCES,
  USER_ACTIONS,
  USER_ROLE_COLORS,
  UserRole,
} from "#customTypes/composedTypes/authTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import {
  AddUserFormValues,
  STAFF_TITLES,
} from "#customTypes/composedTypes/userTypes"
import useHasPermission from "#hooks/newHooks/useHasPermission"
import { COLORS } from "#styles/colors"
import { normalizeEmailFormValue } from "#utils/emailHelpers"
import { filterFormOptions } from "#utils/formHelpers"
import { canAssignRole } from "#utils/permissionsHelpers"

const { Text } = Typography

const AddUserForm = ({
  initialValues,
}: {
  initialValues: AddUserFormValues | undefined | null
}) => {
  const form = Form.useFormInstance<AddUserFormValues>()
  const pinHash = Form.useWatch("pinHash", form)
  const { effectiveRole } = useUserContext()
  const canManageRoles = useHasPermission(RESOURCES.USER, USER_ACTIONS.UPDATE)

  const userId = Form.useWatch("id", form)
  const assignableRoleOptions = USER_ROLE_OPTIONS.filter(({ value }) =>
    canAssignRole(effectiveRole, value as UserRole)
  )

  useEffect(() => {
    if (initialValues?.pinHash) {
      form.setFieldValue("pinHash", initialValues.pinHash)
    }
  }, [initialValues?.pinHash, form])

  return (
    <>
      <Row gutter={0}>
        <Col span={14}>
          <SFormHeader>User Info</SFormHeader>

          <Row gutter={16}>
            <Col span={14}>
              <Form.Item
                name="name"
                label="Display name (used as the main name)"
              >
                <Input placeholder="Leave blank to auto-generate" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={4}>
              <Form.Item name="title" label="Title">
                <Select
                  allowClear
                  placeholder="Mr, Ms, or Mrs"
                  options={STAFF_TITLES.map((value) => ({
                    value,
                    label: value,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item
                name="firstName"
                label="First name"
                rules={[
                  {
                    required: true,
                    message: "Please input the user's first name!",
                  },
                ]}
              >
                <Input placeholder="Enter first name" />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item
                name="lastName"
                label="Last name"
                rules={[
                  {
                    required: true,
                    message: "Please input the user's last name!",
                  },
                ]}
              >
                <Input placeholder="Enter last name" />
              </Form.Item>
            </Col>

            <Col span={5}>
              <Form.Item
                name="dob"
                label="Date of birth"
                rules={[
                  { required: true, message: "Please select a date of birth!" },
                ]}
              >
                <Input placeholder="Date of birth" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="personalPhone"
                label={
                  <Flex center gap={6}>
                    Personal phone
                    <CopyToClipboard
                      dataGetter={() => form.getFieldValue("personalPhone")}
                      compact
                    />
                  </Flex>
                }
                rules={[
                  {
                    required: true,
                    message: "Please input a phone number for the user!",
                  },
                ]}
              >
                <Input placeholder="Enter phone number" />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item
                name="personalEmail"
                label={
                  <Flex center gap={6}>
                    Personal email
                    <CopyToClipboard
                      dataGetter={() => form.getFieldValue("personalEmail")}
                      compact
                    />
                  </Flex>
                }
                rules={[
                  {
                    required: true,
                    message: "Please input an email for the student!",
                  },
                ]}
                normalize={normalizeEmailFormValue}
              >
                <Input
                  placeholder="Enter personal email"
                  type="email"
                  autoComplete="email"
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                name="pronouns"
                label="Pronouns"
                rules={[{ required: true, message: "Please select pronouns!" }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Pronouns"
                  filterOption={filterFormOptions}
                  showSearch
                  options={USER_PRONOUN_OPTIONS}
                />
              </Form.Item>
            </Col>
          </Row>
          {/* 
          <Row gutter={16}>
            <Col span={5}>
              <Form.Item
                name="gender"
                label="Gender"
                rules={[{ required: true, message: "Please select a gender!" }]}
              >
                <Select
                  placeholder="Gender"
                  filterOption={filterFormOptions}
                  showSearch
                  options={USER_GENDER_OPTIONS}
                />
              </Form.Item>
            </Col>
          </Row> */}

          <Row gutter={16}>
            <Col
              span={8}
              style={{
                border: "1px solid red",
                padding: "8px 16px",
                margin: "auto",
              }}
            >
              <SFormHeader>User authorization</SFormHeader>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="role"
                    label="Role"
                    rules={[
                      { required: true, message: "Please select a role!" },
                    ]}
                  >
                    <Select
                      placeholder="Select role"
                      filterOption={filterFormOptions}
                      showSearch
                      disabled={!canManageRoles}
                    >
                      {assignableRoleOptions.map(({ value, label }) => {
                        const color =
                          USER_ROLE_COLORS[
                            value as keyof typeof USER_ROLE_COLORS
                          ]
                        return (
                          <Select.Option
                            key={value}
                            value={value}
                            style={{ color }}
                          >
                            {label}
                          </Select.Option>
                        )
                      })}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>

          <SFormHeader>
            <Flex style={{ alignItems: "center" }} gap={12}>
              Primary Address{" "}
              <CopyToClipboard
                dataGetter={() =>
                  Student.formatAddress(form.getFieldValue("primaryAddress"))
                }
              />
            </Flex>
          </SFormHeader>
          <Row gutter={16}>
            <Col span={10}>
              <Form.Item name={["primaryAddress", "street1"]} label="Street">
                <Input placeholder="Enter street" />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name={["primaryAddress", "street2"]} label="Street 2">
                <Input placeholder="Enter street 2" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={7}>
              <Form.Item name={["primaryAddress", "city"]} label="City">
                <Input placeholder="Enter city" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name={["primaryAddress", "state"]} label="State">
                <Input placeholder="Enter state" />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item name={["primaryAddress", "zipCode"]} label="Zip code">
                <Input placeholder="Enter zip code" />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col span={10}>
          <Row gutter={16}>
            <SFormHeader>Allergies</SFormHeader>
            <Col span={23}>
              <Form.Item name="allergies" rules={[{ required: false }]}>
                <CreatableOptionSelect
                  docType={DOC_TYPES.ALLERGIES}
                  placeholder="Select or add allergies"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={23}>
              <SFormHeader>Tags</SFormHeader>
              <Form.Item name="tags" rules={[{ required: false }]}>
                <CreatableOptionSelect
                  docType={DOC_TYPES.TAGS}
                  placeholder="Select or add tags"
                />
              </Form.Item>
            </Col>
          </Row>

          <SFormHeader>Notes</SFormHeader>
          <Row gutter={16}>
            <Col span={23}>
              <Form.Item name="notes" rules={[{ required: false }]}>
                <Input.TextArea placeholder="Enter notes here" rows={10} />
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>

      <Col span={24} style={{ textAlign: "right", marginBottom: 20 }}>
        {!userId ? (
          <Text type="secondary" style={{ marginRight: 20 }}>
            Save user to generate PIN
          </Text>
        ) : !!pinHash ? (
          <>
            <CheckCircleOutlined style={{ color: COLORS.success }} />{" "}
            <Text style={{ marginRight: 20 }}>PIN already set</Text>
          </>
        ) : (
          <>
            <CloseCircleOutlined style={{ color: COLORS.error }} />{" "}
            <Text style={{ marginRight: 20 }}>PIN not set</Text>
          </>
        )}
        <PinGenerator userId={userId} />
      </Col>

      <Form.Item name="id" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="pinHash" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
    </>
  )
}

export default AddUserForm
